import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  Typography,
  Chip,
} from '@mui/material';

interface MultiSelectPopupProps {
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  width?: string | number;
  title?: string;
}

export default function MultiSelectPopup({
  value = [],
  options = [],
  onChange,
  placeholder = 'Select options...',
  disabled = false,
  width = '100%',
  title,
}: MultiSelectPopupProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tempValue, setTempValue] = useState<string[]>([]);

  // Filter options based on search text
  const filteredOptions = options.filter((option) => option.toLowerCase().includes(searchText.toLowerCase()));

  // Handle dialog open
  const handleOpenDialog = useCallback(() => {
    if (!disabled) {
      setTempValue([...value]);
      setIsDialogOpen(true);
      setSearchText('');
    }
  }, [disabled, value]);

  // Handle dialog close
  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSearchText('');
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    onChange(tempValue);
    handleCloseDialog();
  }, [tempValue, onChange, handleCloseDialog]);

  // Handle option toggle in dialog
  const handleOptionToggle = useCallback(
    (option: string) => {
      const newValue = tempValue.includes(option) ? tempValue.filter((v) => v !== option) : [...tempValue, option];
      setTempValue(newValue);
    },
    [tempValue]
  );

  // Handle chip deletion
  const handleChipDelete = useCallback(
    (chipToDelete: string) => {
      const newValue = value.filter((v) => v !== chipToDelete);
      onChange(newValue);
    },
    [value, onChange]
  );

  // Display text for readonly mode
  const displayText = value.length > 0 ? value.join(', ') : '';

  // If disabled/readonly, show simple text
  if (disabled) {
    return (
      <Box
        sx={{
          width,
          minHeight: '28px',
          padding: '0px 2px',
          fontSize: '0.75rem',
          color: value.length === 0 ? 'rgba(0, 0, 0, 0.6)' : 'rgba(0, 0, 0, 0.87)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          alignItems: 'center',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontWeight: 'normal',
        }}
      >
        {displayText || placeholder}
      </Box>
    );
  }

  return (
    <>
      {/* Clickable button to open dialog */}
      <Button
        onClick={handleOpenDialog}
        variant="outlined"
        size="small"
        fullWidth
        title={title}
        sx={{
          minHeight: '28px',
          justifyContent: 'flex-start',
          textTransform: 'none',
          padding: '0px 2px',
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          fontSize: '0.75rem',
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          fontWeight: 'normal',
          '&:hover': {
            borderColor: '#ccc',
            backgroundColor: 'transparent',
          },
          '&:focus': {
            borderColor: '#1976d2',
            backgroundColor: 'transparent',
          },
        }}
      >
        {value.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, width: '100%' }}>
            {value.slice(0, 2).map((item) => (
              <Chip
                key={item}
                label={item}
                size="small"
                onDelete={(e) => {
                  e.stopPropagation();
                  handleChipDelete(item);
                }}
                sx={{
                  maxWidth: '120px',
                  backgroundColor: 'transparent',
                  border: '1px solid #e0e0e0',
                  fontSize: '0.75rem',
                  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                  fontWeight: 'normal',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            ))}
            {value.length > 2 ? (
              <Chip
                label={`+${value.length - 2} more`}
                size="small"
                variant="outlined"
                sx={{
                  backgroundColor: 'transparent',
                  border: '1px solid #e0e0e0',
                  color: '#666',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              />
            ) : null}
          </Box>
        ) : (
          <span
            style={{
              color: 'rgba(0, 0, 0, 0.6)',
              fontStyle: 'normal',
              fontSize: '0.75rem',
              fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
              fontWeight: 'normal',
            }}
          >
            {placeholder}
          </span>
        )}
      </Button>

      {/* Multi-select dialog */}
      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Select Options</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search options..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ mb: 2 }}
            autoFocus
          />
          <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredOptions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No options found
              </Typography>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = tempValue.includes(option);
                return (
                  <Box
                    key={option}
                    onClick={() => handleOptionToggle(option)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px 0',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <Checkbox checked={isSelected} size="small" />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {option}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
